import { useMemo, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { ClipboardPaste, ImagePlus, Loader2, Plus, Sparkles, Trash2, X } from 'lucide-react';
import { parseResidenceSheet, residenceRowsToSheet, toSheetRows, type ResidenceSheetRow } from '@tally/shared';
import { peopleApi } from '@/services/api';
import { cn } from '@/utils/cn';

const COLUMNS: Array<{ key: keyof ResidenceSheetRow; label: string; width: string; placeholder?: string }> = [
  { key: 'person', label: 'Person', width: 'w-24' },
  { key: 'from', label: 'From', width: 'w-28', placeholder: 'YYYY-MM-DD' },
  { key: 'to', label: 'To', width: 'w-28', placeholder: 'Present' },
  { key: 'unit', label: 'Unit', width: 'w-16' },
  { key: 'streetNumber', label: 'No.', width: 'w-16' },
  { key: 'street', label: 'Street', width: 'w-40' },
  { key: 'city', label: 'City', width: 'w-28' },
  { key: 'region', label: 'Prov/State', width: 'w-20' },
  { key: 'postalCode', label: 'Postal', width: 'w-24' },
  { key: 'country', label: 'Country', width: 'w-24' },
];

const EMPTY_ROW: ResidenceSheetRow = { person: '', from: '', to: '', unit: '', streetNumber: '', street: '', city: '', region: '', postalCode: '', country: '' };
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

/**
 * Paste or drop anything (spreadsheet rows, messy text, or a screenshot); review and fix the rows in an
 * editable table; then import. Clean spreadsheet rows are read locally; everything else can be read with AI.
 */
export function SmartImport({ onImported }: { onImported: (message: string) => void }) {
  const [text, setText] = useState('');
  const [image, setImage] = useState<{ mimeType: string; data: string; preview: string } | null>(null);
  const [rows, setRows] = useState<ResidenceSheetRow[] | null>(null);
  const [notes, setNotes] = useState<string[]>([]);
  const [dragging, setDragging] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  // Clean spreadsheet rows parse instantly, with no AI involved.
  const local = useMemo(() => (text.trim() && !image ? parseResidenceSheet(text) : null), [text, image]);
  const localIsClean = !!local && local.rows.length > 0 && local.errors.length === 0;

  // Live validation of the editable rows, with the same parser the server uses.
  const validation = useMemo(() => (rows ? parseResidenceSheet(residenceRowsToSheet(rows)) : null), [rows]);
  const rowErrors = new Map((validation?.errors ?? []).map((e) => [e.row - 2, e.message])); // row 1 is the header

  const readWithAi = useMutation({
    mutationFn: () => peopleApi.importAi({ text: text.trim() || undefined, image: image ? { mimeType: image.mimeType, data: image.data } : undefined }),
    onSuccess: (res) => {
      const parsed = parseResidenceSheet(res.data.text);
      setRows(parsed.rows.length || parsed.errors.length ? sheetRowsFromText(res.data.text) : []);
      setNotes(res.data.notes ?? []);
      setProblem(null);
    },
    onError: (err: Error) => setProblem(err.message),
  });

  const save = useMutation({
    mutationFn: () => peopleApi.importSheet(residenceRowsToSheet(rows ?? []), false),
    onSuccess: (res) => {
      const imported = res.data.imported!;
      onImported(`Imported ${imported.residencesImported} addresses${imported.peopleCreated.length ? `; added ${imported.peopleCreated.join(', ')}` : ''}. Trips will be recalculated.`);
      reset();
    },
    onError: (err: Error) => setProblem(err.message),
  });

  function reset() {
    setText('');
    setImage(null);
    setRows(null);
    setNotes([]);
    setProblem(null);
  }

  async function acceptFile(file: File) {
    setProblem(null);
    if (file.type.startsWith('image/')) {
      if (file.size > MAX_IMAGE_BYTES) return setProblem('That image is larger than 5 MB. Try a tighter screenshot.');
      const dataUrl = await readAs(file, 'dataUrl');
      setImage({ mimeType: file.type, data: dataUrl.split(',')[1], preview: dataUrl });
      setRows(null);
    } else {
      setText(await readAs(file, 'text'));
      setImage(null);
      setRows(null);
    }
  }

  function onPaste(e: React.ClipboardEvent) {
    const file = [...e.clipboardData.files].find((f) => f.type.startsWith('image/'));
    if (file) {
      e.preventDefault();
      void acceptFile(file);
    }
  }

  const update = (i: number, key: keyof ResidenceSheetRow, value: string) =>
    setRows((current) => current!.map((r, j) => (j === i ? { ...r, [key]: value } : r)));

  if (rows) {
    const errorCount = validation?.errors.length ?? 0;
    return (
      <div className="mb-4 space-y-3 rounded-lg border border-border bg-muted/30 p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm font-medium">Check the rows, fix anything that's off, then import.</p>
          <button onClick={reset} className="text-sm text-muted-foreground hover:text-foreground">Start over</button>
        </div>
        {notes.length > 0 && (
          <ul className="list-inside list-disc text-xs text-amber-700 dark:text-amber-400">
            {notes.map((n, i) => <li key={i}>{n}</li>)}
          </ul>
        )}
        <div className="overflow-x-auto">
          <table className="text-xs">
            <thead className="text-left text-muted-foreground">
              <tr>{COLUMNS.map((c) => <th key={c.key} className="px-1 py-1 font-medium">{c.label}</th>)}<th /></tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={i} className={cn(rowErrors.has(i) && 'bg-destructive/5')}>
                  {COLUMNS.map((c) => (
                    <td key={c.key} className="px-0.5 py-0.5">
                      <input
                        value={row[c.key]}
                        placeholder={c.placeholder}
                        onChange={(e) => update(i, c.key, e.target.value)}
                        aria-label={`${c.label}, row ${i + 1}`}
                        className={cn('rounded border border-input bg-background px-1.5 py-1', c.width)}
                      />
                    </td>
                  ))}
                  <td>
                    <button onClick={() => setRows(rows.filter((_, j) => j !== i))} className="rounded p-1 text-muted-foreground hover:text-destructive" aria-label={`Remove row ${i + 1}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {errorCount > 0 && (
          <ul className="space-y-0.5 text-xs text-destructive">
            {[...rowErrors.entries()].map(([i, msg]) => <li key={i}>Row {i + 1}: {msg}</li>)}
          </ul>
        )}
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={() => setRows([...rows, { ...EMPTY_ROW, person: rows[rows.length - 1]?.person ?? '' }])} className="flex items-center gap-1 rounded-lg border border-border px-3 py-1.5 text-xs hover:bg-accent">
            <Plus className="h-3.5 w-3.5" /> Add row
          </button>
          <button
            onClick={() => save.mutate()}
            disabled={errorCount > 0 || rows.length === 0 || save.isPending}
            className="rounded-lg bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {save.isPending ? 'Importing…' : `Import ${rows.length} address${rows.length === 1 ? '' : 'es'}`}
          </button>
          <span className="text-xs text-muted-foreground">Replaces the home history of each person listed.</span>
        </div>
        {problem && <p className="text-sm text-destructive">{problem}</p>}
      </div>
    );
  }

  return (
    <div className="mb-4 space-y-2">
      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => { e.preventDefault(); setDragging(false); const f = e.dataTransfer.files[0]; if (f) void acceptFile(f); }}
        className={cn('rounded-lg border-2 border-dashed p-3 transition-colors', dragging ? 'border-primary bg-primary/5' : 'border-border')}
      >
        {image ? (
          <div className="flex items-start gap-3">
            <img src={image.preview} alt="Attached screenshot" className="max-h-40 rounded border border-border" />
            <button onClick={() => setImage(null)} className="rounded p-1 text-muted-foreground hover:text-foreground" aria-label="Remove image"><X className="h-4 w-4" /></button>
          </div>
        ) : (
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            onPaste={onPaste}
            rows={5}
            placeholder="Paste your address history here — rows copied from a spreadsheet, text from a document, or a screenshot. You can also drop a file."
            className="w-full resize-y bg-transparent text-sm focus:outline-none"
          />
        )}
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <button onClick={() => fileInput.current?.click()} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
            <ImagePlus className="h-3.5 w-3.5" /> Attach screenshot or file
          </button>
          <input ref={fileInput} type="file" accept="image/*,.csv,.tsv,.txt" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) void acceptFile(f); e.target.value = ''; }} />
          <span className="text-xs text-muted-foreground"><ClipboardPaste className="mr-1 inline h-3 w-3" />Tip: in Google Sheets, select the rows and copy (⌘C), then paste here.</span>
        </div>
      </div>

      {localIsClean && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-green-700 dark:text-green-400">Found {local!.rows.length} addresses.</span>
          <button onClick={() => setRows(toSheetRows(local!.rows))} className="rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90">Review rows</button>
        </div>
      )}

      {(image || (text.trim() && !localIsClean)) && (
        <div className="space-y-1">
          {!image && local && (
            <p className="text-xs text-muted-foreground">
              {local.rows.length > 0 ? `${local.rows.length} rows look right, ${local.errors.length} don't. ` : "This doesn't look like spreadsheet rows. "}
              You can let AI read it, or{' '}
              <button onClick={() => setRows(local.rows.length ? toSheetRows(local.rows) : [{ ...EMPTY_ROW }])} className="underline">fix the rows by hand</button>.
            </p>
          )}
          <button
            onClick={() => readWithAi.mutate()}
            disabled={readWithAi.isPending}
            className="flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {readWithAi.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            {readWithAi.isPending ? 'Reading…' : 'Read with AI'}
          </button>
          <p className="text-xs text-muted-foreground">AI reading sends this text or image to Google Gemini for this one request. You review every row before anything is saved.</p>
        </div>
      )}
      {problem && <p className="text-sm text-destructive">{problem}</p>}
    </div>
  );
}

/** Editable rows from sheet text, keeping rows that fail validation so they can be fixed in place. */
function sheetRowsFromText(sheetText: string): ResidenceSheetRow[] {
  const lines = sheetText.split('\n').slice(1);
  return lines.map((line) => {
    const cells = splitCells(line);
    return {
      person: cells[0] ?? '', from: cells[1] ?? '', to: cells[2] ?? '', unit: cells[3] ?? '', streetNumber: cells[4] ?? '',
      street: cells[5] ?? '', city: cells[6] ?? '', region: cells[7] ?? '', postalCode: cells[8] ?? '', country: cells[9] ?? '',
    };
  });
}

function splitCells(line: string): string[] {
  const out: string[] = [];
  let cell = '';
  let quoted = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (quoted) {
      if (ch === '"' && line[i + 1] === '"') { cell += '"'; i++; } else if (ch === '"') quoted = false; else cell += ch;
    } else if (ch === '"' && cell === '') quoted = true;
    else if (ch === '\t') { out.push(cell); cell = ''; } else cell += ch;
  }
  out.push(cell);
  return out;
}

function readAs(file: File, kind: 'text' | 'dataUrl'): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    if (kind === 'text') reader.readAsText(file);
    else reader.readAsDataURL(file);
  });
}
